import type { DepositRequest } from '../types/finance';
import type { Commitment } from '../types/commitments';

/** A commitment in `drafting` is sitting on the client's signature. Past
 *  this many days we flag it so a never-signed draft doesn't wait
 *  invisibly forever (no auto-expiry — the agent still owns the follow-up). */
export const STALE_DRAFTING_DAYS = 7;

/** Whole days between `iso` and `now` (never negative; 0 for an invalid or
 *  future date). */
export function daysSince(iso: string, now: Date = new Date()): number {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((now.getTime() - then) / 86_400_000));
}

/** Label + staleness for a `drafting` commitment, e.g.
 *  `{ label: 'awaiting signature · 3 days', stale: false }`. Surfaces how
 *  long it has been waiting on the client so the agent can chase it. */
export function draftingAge(
  createdAtIso: string,
  now: Date = new Date(),
): { label: string; stale: boolean } {
  const d = daysSince(createdAtIso, now);
  const unit = d === 1 ? 'day' : 'days';
  const span = d <= 0 ? 'today' : `${d} ${unit}`;
  return { label: `awaiting signature · ${span}`, stale: d >= STALE_DRAFTING_DAYS };
}

/**
 * Sum decimal-string money amounts WITHOUT float drift.
 *
 * Backend `Numeric(24,8)` amounts are serialised as strings precisely to
 * preserve precision; `Number(...)`-summing them reintroduces IEEE-754
 * rounding (e.g. `0.1 + 0.2 = 0.30000000000000004`). We parse each value as
 * a scaled BigInt (8 fractional digits = the ledger precision), sum exactly,
 * then reformat to a fixed-`scale` decimal string. No `Number()` is involved.
 */
export function sumDecimalStrings(values: string[], scale = 8): string {
  const factor = 10n ** BigInt(scale);
  let acc = 0n;
  for (const raw of values) {
    const n = raw.trim();
    if (n === '') continue;
    const negative = n.startsWith('-');
    const [intPart, fracPart = ''] = n.replace(/^[+-]/, '').split('.');
    const frac = (fracPart + '0'.repeat(scale)).slice(0, scale);
    const scaled = BigInt(intPart || '0') * factor + BigInt(frac || '0');
    acc += negative ? -scaled : scaled;
  }
  const negative = acc < 0n;
  const abs = negative ? -acc : acc;
  const intStr = (abs / factor).toString();
  const fracStr = (abs % factor).toString().padStart(scale, '0');
  return `${negative ? '-' : ''}${intStr}.${fracStr}`;
}

/** The pending deposits for a single product, with their summed amount as a
 *  precise decimal string. Used to preview exactly which deposits a
 *  commitment termination will auto-reject so the manager isn't deciding
 *  blind. One product = one currency, so `currency` is the first matched row's. */
export function pendingDepositsForProduct(
  items: DepositRequest[],
  productId: string,
): { items: DepositRequest[]; count: number; total: string; currency: string | null } {
  const matched = items.filter((d) => d.product_id === productId);
  return {
    items: matched,
    count: matched.length,
    total: sumDecimalStrings(matched.map((d) => d.amount)),
    currency: matched[0]?.currency_code ?? null,
  };
}

// ── Commitment display helpers (shared by MyCommitmentsCard + the
//    reciprocal commitment card on the client's contract page) ──────────

/** `day_of_week` is 0=Mon … 6=Sun (NOT JS getDay()). */
export const COMMITMENT_DOW_LABEL = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

const COMMITMENT_STATUS_LABEL: Record<string, string> = {
  drafting: 'Awaiting your signature',
  pending_manager: 'Awaiting manager approval',
  active: 'Active',
  paused: 'Paused',
  pending_cancellation: 'Cancellation under review',
  cancelled: 'Cancelled',
  terminated: 'Terminated',
  completed: 'Completed',
};

/** Client-facing status label; falls back to the raw slug for any unknown. */
export function commitmentStatusLabel(status: string): string {
  return COMMITMENT_STATUS_LABEL[status] ?? status;
}

/** Plain-language cadence, e.g. "Every Wed" / "Day 15 of every month". */
export function cadenceText(c: Commitment): string {
  if (c.cadence === 'one_time') return `One-time on ${c.start_date}`;
  if (c.cadence === 'weekly') {
    return `Every ${c.day_of_week === null ? '—' : COMMITMENT_DOW_LABEL[c.day_of_week]}`;
  }
  if (c.cadence === 'monthly') return `Day ${c.day_of_month ?? '—'} of every month`;
  return c.cadence;
}

/** Bounded commitments (end_date set) render "N of M contributions fulfilled";
 *  open-ended ones render "N contribution[s] fulfilled" — no progress bar
 *  implying a finish line (engagement decision: count only, no streaks). */
export function completionLine(c: Commitment): string {
  if (c.end_date && c.expected_count > 0) {
    return `${c.fulfilled_count} of ${c.expected_count} contributions fulfilled`;
  }
  const suffix = c.fulfilled_count === 1 ? '' : 's';
  return `${c.fulfilled_count} contribution${suffix} fulfilled`;
}

// ── next-contribution derivation (a soft "what's next" hint, NOT the
//    authoritative expected_deposits schedule the backend owns) ──────────

function startOfUtcDay(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/** Parse a 'YYYY-MM-DD' (or longer ISO) string to UTC midnight, or null. */
function parseUtcDate(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Next occurrence (incl. today) of a weekly day_of_week (0=Mon … 6=Sun). */
function nextWeeklyDate(today: Date, dayOfWeek: number): Date {
  const targetJsDay = (dayOfWeek + 1) % 7; // our 0=Mon → JS 1; our 6=Sun → JS 0
  const diff = (targetJsDay - today.getUTCDay() + 7) % 7;
  const result = new Date(today);
  result.setUTCDate(result.getUTCDate() + diff);
  return result;
}

/** Next occurrence (incl. today) of a monthly day-of-month, clamped to the
 *  month's length (e.g. day 31 → Feb 28/29). */
function nextMonthlyDate(today: Date, dayOfMonth: number): Date {
  const clamp = (y: number, mo: number, d: number): Date => {
    const lastDay = new Date(Date.UTC(y, mo + 1, 0)).getUTCDate();
    return new Date(Date.UTC(y, mo, Math.min(d, lastDay)));
  };
  const y = today.getUTCFullYear();
  const mo = today.getUTCMonth();
  const thisMonth = clamp(y, mo, dayOfMonth);
  if (thisMonth.getTime() >= today.getTime()) return thisMonth;
  return clamp(mo === 11 ? y + 1 : y, (mo + 1) % 12, dayOfMonth);
}

/** Next expected contribution date (YYYY-MM-DD) for a live commitment, or null
 *  when none applies: a terminal status, a past one-time, or a next occurrence
 *  that would fall after `end_date`. Derived client-side — see note above. */
export function nextContributionDate(c: Commitment, asOf: Date = new Date()): string | null {
  if (c.status === 'cancelled' || c.status === 'completed' || c.status === 'terminated') {
    return null;
  }
  const today = startOfUtcDay(asOf);
  let next: Date | null = null;
  if (c.cadence === 'one_time') {
    const start = parseUtcDate(c.start_date);
    next = start && start.getTime() >= today.getTime() ? start : null;
  } else if (c.cadence === 'weekly' && c.day_of_week !== null) {
    next = nextWeeklyDate(today, c.day_of_week);
  } else if (c.cadence === 'monthly' && c.day_of_month !== null) {
    next = nextMonthlyDate(today, c.day_of_month);
  }
  if (!next) return null;
  if (c.end_date) {
    const end = parseUtcDate(c.end_date);
    if (end && next.getTime() > end.getTime()) return null;
  }
  return toIsoDate(next);
}
